import type { ThumbsupUnit } from './services/api';

export interface ThumbsupViewedHistoryEntry {
  id: number;
  projectId: string;
  unitNumber: string;
  projectName: string;
}

export const THUMBSUP_LAST_VIEWED_STORAGE_KEY = 'superPicker:thumbsupLastViewed';

export const buildThumbsupViewedHistoryEntry = (
  item: ThumbsupUnit,
): ThumbsupViewedHistoryEntry => ({
  id: item.id,
  projectId: item.ProjectId,
  unitNumber: item.UnitNumber,
  projectName: item.projectName,
});

export const normalizeThumbsupViewedHistory = (
  value: unknown,
): ThumbsupViewedHistoryEntry[] => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((entry): entry is ThumbsupViewedHistoryEntry => {
      if (!entry || typeof entry !== 'object') return false;
      const candidate = entry as Partial<ThumbsupViewedHistoryEntry>;
      return (
        typeof candidate.id === 'number' &&
        typeof candidate.projectId === 'string' &&
        typeof candidate.unitNumber === 'string' &&
        typeof candidate.projectName === 'string'
      );
    })
    .slice(0, 2);
};

export const readThumbsupViewedHistory = (
  storage: Pick<Storage, 'getItem'> | null | undefined = typeof window !== 'undefined' ? window.localStorage : undefined,
): ThumbsupViewedHistoryEntry[] => {
  if (!storage) return [];

  try {
    const raw = storage.getItem(THUMBSUP_LAST_VIEWED_STORAGE_KEY);
    if (!raw) return [];
    return normalizeThumbsupViewedHistory(JSON.parse(raw));
  } catch {
    return [];
  }
};

export const saveViewedThumbsupUnit = (
  item: ThumbsupUnit,
  history: ThumbsupViewedHistoryEntry[],
): ThumbsupViewedHistoryEntry[] => {
  const nextEntry = buildThumbsupViewedHistoryEntry(item);
  return [
    nextEntry,
    ...history.filter((entry) => entry.id !== nextEntry.id),
  ].slice(0, 2);
};

export const persistThumbsupViewedHistory = (
  history: ThumbsupViewedHistoryEntry[],
  storage: Pick<Storage, 'setItem'> | null | undefined = typeof window !== 'undefined' ? window.localStorage : undefined,
): void => {
  if (!storage) return;

  try {
    storage.setItem(
      THUMBSUP_LAST_VIEWED_STORAGE_KEY,
      JSON.stringify(history.slice(0, 2)),
    );
  } catch {
    // Ignore storage failures so unit navigation still works.
  }
};

export const getLatestValidViewedThumbsupUnit = (
  history: ThumbsupViewedHistoryEntry[],
  currentUnits: ThumbsupUnit[],
): ThumbsupUnit | null => {
  const unitsById = new Map(currentUnits.map((unit) => [unit.id, unit]));

  for (const entry of history) {
    const match = unitsById.get(entry.id);
    if (match) return match;
  }

  return null;
};
